import {HttpErrorResponse} from '@angular/common/http';
import {
    BrowserAuthError,
    BrowserAuthErrorCodes,
    InteractionRequiredAuthError
} from '@azure/msal-browser';
import {protectedResources} from '../../../auth.config';
import {ShellLoadFailureKind} from '../shell-load-failure.util';

export type MsalFailureDiagnosticPhase = 'missing-account' | 'acquire-token-silent';
export type MsalFailureRouteClass = 'ai-chatbots' | 'webinars' | 'enrich-setup' | 'other';
export type MsalFailureResourceClass = 'ai' | 'webinars' | 'enrich' | 'connect' | 'other';
export type MsalFailureCategory =
    | 'missing-account'
    | 'interaction-required'
    | 'timed-out'
    | 'http-status'
    | 'browser-auth'
    | 'unknown';
export type AllowedMsalErrorCode = 'interaction_required' | typeof BrowserAuthErrorCodes.timedOut;

export interface MsalFailureDiagnostic {
    phase: MsalFailureDiagnosticPhase;
    epoch: number;
    forwarded: boolean;
    interactiveRecoveryTriggered: boolean;
    routeClass: MsalFailureRouteClass;
    resourceClass: MsalFailureResourceClass;
    shellFailure: ShellLoadFailureKind;
    category: MsalFailureCategory;
    status?: number;
    errorCode?: AllowedMsalErrorCode;
}

const ALLOWED_MSAL_ERROR_CODES = new Set<AllowedMsalErrorCode>([
    'interaction_required',
    BrowserAuthErrorCodes.timedOut
]);

const readStatus = (error: unknown): number | undefined => {
    if (error instanceof HttpErrorResponse) {
        return typeof error.status === 'number' ? error.status : undefined;
    }

    if (error && typeof error === 'object' && typeof (error as {status?: unknown}).status === 'number') {
        return (error as {status: number}).status;
    }

    return undefined;
};

const classifyCategory = (phase: MsalFailureDiagnosticPhase, error: unknown): MsalFailureCategory => {
    if (phase === 'missing-account') {
        return 'missing-account';
    }

    if (error instanceof InteractionRequiredAuthError) {
        return 'interaction-required';
    }

    if (error instanceof BrowserAuthError && error.errorCode === BrowserAuthErrorCodes.timedOut) {
        return 'timed-out';
    }

    if (typeof readStatus(error) === 'number') {
        return 'http-status';
    }

    if (error instanceof BrowserAuthError) {
        return 'browser-auth';
    }

    return 'unknown';
};

const readAllowedErrorCode = (error: unknown): AllowedMsalErrorCode | undefined => {
    if (!(error instanceof BrowserAuthError || error instanceof InteractionRequiredAuthError)) {
        return undefined;
    }

    return ALLOWED_MSAL_ERROR_CODES.has(error.errorCode as AllowedMsalErrorCode)
        ? error.errorCode as AllowedMsalErrorCode
        : undefined;
};

const classifyRouteClass = (): MsalFailureRouteClass => {
    const path = typeof window === 'undefined' || !window.location
        ? '/'
        : window.location.pathname.replace(/\/+$/, '') || '/';

    if (path === '/ai/chatbots' || path.startsWith('/ai/chatbots/')) {
        return 'ai-chatbots';
    }

    if (path === '/webinars' || path.startsWith('/webinars/')) {
        return 'webinars';
    }

    if (path === '/enrich/setup' || path.startsWith('/enrich/setup/')) {
        return 'enrich-setup';
    }

    return 'other';
};

const normalizeUrl = (url?: string): string => (url ?? '').replace(/\/+$/, '');

const classifyResourceClass = (requestUrl: string): MsalFailureResourceClass => {
    const normalizedUrl = normalizeUrl(requestUrl);
    if (!normalizedUrl) {
        return 'other';
    }

    const matchesEndpoint = (endpoint?: string): boolean => {
        const normalizedEndpoint = normalizeUrl(endpoint);
        return normalizedEndpoint !== '' && normalizedUrl.startsWith(normalizedEndpoint);
    };

    if (matchesEndpoint(protectedResources['api']?.endpoint)) {
        return 'ai';
    }

    if (matchesEndpoint(protectedResources['enrich']?.endpoint)) {
        return 'enrich';
    }

    if (matchesEndpoint(protectedResources['webinars']?.endpoint)) {
        return 'webinars';
    }

    if (matchesEndpoint(protectedResources['connect']?.endpoint)) {
        return 'connect';
    }

    return 'other';
};

export const buildMsalFailureDiagnostic = ({
    phase,
    epoch,
    forwarded,
    interactiveRecoveryTriggered,
    requestUrl,
    shellFailure,
    error
}: {
    phase: MsalFailureDiagnosticPhase;
    epoch: number;
    forwarded: boolean;
    interactiveRecoveryTriggered: boolean;
    requestUrl: string;
    shellFailure: ShellLoadFailureKind;
    error?: unknown;
}): MsalFailureDiagnostic => {
    const diagnostic: MsalFailureDiagnostic = {
        phase,
        epoch,
        forwarded,
        interactiveRecoveryTriggered,
        routeClass: classifyRouteClass(),
        resourceClass: classifyResourceClass(requestUrl),
        shellFailure,
        category: classifyCategory(phase, error)
    };

    const status = readStatus(error);
    if (typeof status === 'number') {
        diagnostic.status = status;
    }

    const errorCode = readAllowedErrorCode(error);
    if (errorCode) {
        diagnostic.errorCode = errorCode;
    }

    return diagnostic;
};
